import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { AppShell, TopHeader } from "../components/AppShell";
import { Button, Card, Pill, SectionLabel } from "../components/ui";
import { Ic } from "../components/icons";
import { Cadastre } from "../components/Cadastre";
import { SignInDialog } from "../components/SignInDialog";
import { useNav } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import {
  runPipeline,
  PipelineError,
  extractReglementFromUserUpload,
  refetchFootprint,
  type StageEvent,
  type StageKey,
  type StageStatus,
  type PipelineResult,
  type FootprintSummary,
} from "../lib/pipeline";
import {
  createFiche,
  newFicheId,
  updateFicheParcel,
  updateFicheParcelAndExisting,
  type ExistingProperty,
} from "../lib/fiches";
import { PluUploadModal } from "../components/PluUploadModal";
import {
  extractListingFromPdf,
  ListingExtractionError,
} from "../lib/extractListing";
import {
  consumeCreditForFiche,
  refundCreditForFiche,
} from "../lib/useConsumeCredit";
import { useEntitlements } from "../lib/useEntitlements";
import { CreditsExhaustedModal } from "../components/CreditsExhaustedModal";
import {
  PipelineErrorModal,
  type RefundState,
} from "../components/PipelineErrorModal";
import { ParcelPickerMap } from "../components/ParcelPickerMap";
import {
  mergeParcels,
  parcelComponentIds,
  parcelLabel,
} from "../lib/parcelMerge";
import type { GatingReason } from "../lib/billing";
import type { CadastreParcel, ListingExtraction, GeocodeResult } from "../lib/types";
import {
  useBanSuggestions,
  suggestionToGeocodeResult,
  type BanSuggestion,
} from "../lib/ban";

// Step 2 form state — kept as strings so inputs stay controlled even when empty.
type ExistingForm = {
  type: "Maison" | "Immeuble" | "Terrain";
  surface_habitable_m2: string;
  emprise_m2: string;
  hauteur_m: string;
  niveaux: string;
  year_built: string;
  extension_deposee: boolean;
  monument_proximite: boolean;
  jardin_classe: boolean;
};

const defaultExisting: ExistingForm = {
  type: "Maison",
  surface_habitable_m2: "",
  emprise_m2: "",
  hauteur_m: "",
  niveaux: "R+1",
  year_built: "",
  extension_deposee: false,
  monument_proximite: false,
  jardin_classe: false,
};

// Merge BD TOPO footprint into the agent's manual input. Agent input always
// wins — BD TOPO only fills fields the agent left empty. The fields we
// filled are tagged in `sources` so the fiche UI can surface a "BD TOPO"
// indicator, inviting the agent to double-check.
/**
 * Derive the GPU-style `typezone` value (U / AU / A / N) from a raw zone
 * code. GPU normally fills this on the zone-urba feature, but the manual-
 * upload path doesn't go through GPU — so we infer it from the code
 * prefix. Matches the four `typezone` enum values defined by the IGN
 * GPU schema.
 *
 * Examples : "UC" → "U", "1AU" → "AU", "AUa" → "AU", "Nh" → "N",
 * "Ap" → "A". Returns "" for codes that don't fit the pattern (very
 * rare ; the Hero falls back to "—" anyway).
 */
function deriveTypezoneFromCode(zoneCode: string): string {
  const upper = zoneCode.toUpperCase().trim();
  // Numeric-prefixed AU (1AU, 2AU…) — most common AU pattern in France.
  if (/^\d+AU/.test(upper)) return "AU";
  if (upper.startsWith("AU")) return "AU";
  if (upper.startsWith("U")) return "U";
  if (upper.startsWith("A")) return "A";
  if (upper.startsWith("N")) return "N";
  return "";
}

/**
 * One-line digest for the collapsed Step 2 header. Picks whichever
 * fields the user filled — type is always present, dimensions are
 * optional. Empty form → just the type.
 */
function existingSummary(f: ExistingForm): string {
  const bits: string[] = [f.type];
  if (f.surface_habitable_m2) bits.push(`${f.surface_habitable_m2} m² SH`);
  if (f.emprise_m2) bits.push(`${f.emprise_m2} m² emprise`);
  if (f.hauteur_m) bits.push(`${f.hauteur_m} m`);
  if (f.niveaux) bits.push(f.niveaux);
  if (f.year_built) bits.push(f.year_built);
  return bits.join(" · ");
}

function mergeFootprintIntoExisting(
  existing: ExistingProperty,
  footprint: FootprintSummary | null,
): ExistingProperty {
  if (!footprint) return existing;
  const filledEmprise =
    existing.emprise_m2 == null && footprint.builtFootprint_m2 > 0;
  const filledHauteur =
    existing.hauteur_m == null && footprint.maxHeight_m != null;
  if (!filledEmprise && !filledHauteur) return existing;
  const sources: NonNullable<ExistingProperty["sources"]> = {
    ...(existing.sources ?? {}),
  };
  if (filledEmprise) sources.emprise_m2 = "bdtopo";
  if (filledHauteur) sources.hauteur_m = "bdtopo";
  return {
    ...existing,
    emprise_m2: filledEmprise ? footprint.builtFootprint_m2 : existing.emprise_m2,
    hauteur_m: filledHauteur ? footprint.maxHeight_m : existing.hauteur_m,
    sources,
  };
}

/**
 * Like `mergeFootprintIntoExisting`, but **overwrites** fields whose previous
 * value was BD TOPO-sourced (`sources.X === "bdtopo"`). User-edited values
 * are preserved. Called when the user changes parcel after the fiche was
 * created — the BD TOPO numbers are tied to the parcel geometry, so they
 * must follow the parcel switch.
 */
function refreshBdTopoFields(
  existing: ExistingProperty,
  footprint: FootprintSummary | null,
): ExistingProperty {
  if (!footprint) return existing;
  const sources = existing.sources ?? {};
  const empriseFromBdTopo =
    sources.emprise_m2 === "bdtopo" || existing.emprise_m2 == null;
  const hauteurFromBdTopo =
    sources.hauteur_m === "bdtopo" || existing.hauteur_m == null;
  // Only touch the value if BD TOPO has something to say AND we're allowed
  // to (was bdtopo-sourced or empty). Otherwise keep the user's edit.
  const newEmprise =
    empriseFromBdTopo && footprint.builtFootprint_m2 > 0
      ? footprint.builtFootprint_m2
      : existing.emprise_m2;
  const newHauteur =
    hauteurFromBdTopo && footprint.maxHeight_m != null
      ? footprint.maxHeight_m
      : existing.hauteur_m;
  const nextSources: NonNullable<ExistingProperty["sources"]> = { ...sources };
  if (empriseFromBdTopo && footprint.builtFootprint_m2 > 0) {
    nextSources.emprise_m2 = "bdtopo";
  }
  if (hauteurFromBdTopo && footprint.maxHeight_m != null) {
    nextSources.hauteur_m = "bdtopo";
  }
  return {
    ...existing,
    emprise_m2: newEmprise,
    hauteur_m: newHauteur,
    sources: nextSources,
  };
}

function toExistingProperty(f: ExistingForm): ExistingProperty {
  const num = (s: string) => {
    const n = Number(s.replace(",", "."));
    return Number.isFinite(n) && s.trim() !== "" ? n : null;
  };
  const notesBits: string[] = [];
  if (f.extension_deposee) notesBits.push("Extension déjà déposée");
  if (f.monument_proximite) notesBits.push("Monument historique à proximité");
  if (f.jardin_classe) notesBits.push("Jardin arboré classé");
  return {
    type: f.type,
    surface_habitable_m2: num(f.surface_habitable_m2),
    emprise_m2: num(f.emprise_m2),
    hauteur_m: num(f.hauteur_m),
    niveaux: f.niveaux.trim() || null,
    year_built: num(f.year_built),
    notes: notesBits.join(" · "),
  };
}

export default function Nouvelle() {
  const { go } = useNav();
  const { user } = useAuth();
  const entitlements = useEntitlements();
  const [step, setStep] = useState(1);
  const [addr, setAddr] = useState("");
  const [existing, setExisting] = useState<ExistingForm>(defaultExisting);
  const [focused, setFocused] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  /** Quand non-null, la modale d'upsell est affichée. Posté par
   *  `GenerationPanel` quand `consumeCreditForFiche` refuse, ou par le bouton
   *  "Générer la fiche" quand on sait déjà qu'il n'y a plus de crédits. */
  const [gatingModal, setGatingModal] = useState<GatingReason>(null);
  const [extraction, setExtraction] = useState<ListingExtraction | null>(null);
  const [prefilledFields, setPrefilledFields] = useState<Set<keyof ExistingForm>>(
    new Set(),
  );
  // When the user picks a BAN suggestion we snapshot its geocode payload.
  // The pipeline uses it to skip the first Cloud Function call, and the
  // picker UI highlights the picked row. Typing anything new clears it
  // because the text no longer matches a validated BAN record.
  const [pickedGeocode, setPickedGeocode] = useState<GeocodeResult | null>(
    null,
  );
  // Manual override of the auto-collapse behaviour. When `step > N` the
  // step N card collapses to a one-line summary by default ; the user
  // can still click it to expand and re-edit. Only steps 1 & 2 collapse
  // (step 3 is always the active workspace).
  const [expandedSteps, setExpandedSteps] = useState<Record<1 | 2, boolean>>({
    1: false,
    2: false,
  });
  const isStepExpanded = (n: 1 | 2) => step === n || expandedSteps[n];
  // When the user advances past a step, drop any manual expand override
  // so the next time they reach this step (via Retour) it starts fresh
  // collapsed/expanded based on `step` alone.
  useEffect(() => {
    setExpandedSteps((prev) => {
      const next = { ...prev };
      if (step > 1 && prev[1]) next[1] = false;
      if (step > 2 && prev[2]) next[2] = false;
      return next;
    });
  }, [step]);

  // Apply an extraction to the form. Only fills fields the user hasn't already
  // typed into — we don't want a PDF upload to wipe manual edits.
  function applyExtraction(ex: ListingExtraction) {
    setExtraction(ex);
    if (ex.address && !addr.trim()) setAddr(ex.address);

    const toStr = (n: number | null) => (n !== null ? String(n) : "");
    const touched = new Set<keyof ExistingForm>();
    setExisting((prev) => {
      const next = { ...prev };
      if (ex.property.type) {
        next.type = ex.property.type;
        touched.add("type");
      }
      const tryNum = (
        k: "surface_habitable_m2" | "emprise_m2" | "hauteur_m" | "year_built",
        v: number | null,
      ) => {
        if (v !== null && !prev[k]) {
          next[k] = toStr(v);
          touched.add(k);
        }
      };
      tryNum("surface_habitable_m2", ex.property.surface_habitable_m2);
      tryNum("emprise_m2", ex.property.emprise_m2);
      tryNum("hauteur_m", ex.property.hauteur_m);
      tryNum("year_built", ex.property.year_built);
      if (ex.property.niveaux && prev.niveaux === defaultExisting.niveaux) {
        next.niveaux = ex.property.niveaux;
        touched.add("niveaux");
      }
      if (ex.flags.extension_deposee !== null) {
        next.extension_deposee = ex.flags.extension_deposee;
        touched.add("extension_deposee");
      }
      if (ex.flags.monument_proximite !== null) {
        next.monument_proximite = ex.flags.monument_proximite;
        touched.add("monument_proximite");
      }
      if (ex.flags.jardin_classe !== null) {
        next.jardin_classe = ex.flags.jardin_classe;
        touched.add("jardin_classe");
      }
      return next;
    });
    setPrefilledFields(touched);
    // Surface the prefilled form to the user — no point keeping them on step 1.
    setStep((s) => Math.max(s, 2));
  }

  // Enable "continue" once user has typed something address-shaped — OR picked
  // a BAN suggestion (which we fully trust even for short labels like a
  // municipality match).
  const addrLooksOk =
    !!pickedGeocode || (addr.trim().length > 6 && /\d/.test(addr));

  // Live BAN autocomplete. Debounced + aborted inside the hook; safe to feed
  // raw input on every keystroke. When the user has already picked a
  // suggestion and hasn't edited the text since, we don't re-query.
  const addrMatchesPick =
    pickedGeocode !== null && pickedGeocode.label === addr;
  const banState = useBanSuggestions(addrMatchesPick ? "" : addr);

  function patch<K extends keyof ExistingForm>(k: K, v: ExistingForm[K]) {
    setExisting((prev) => ({ ...prev, [k]: v }));
  }

  return (
    <AppShell active="nouvelle">
      <TopHeader>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => go("dashboard")}
          icon={<Ic.Cross size={13} />}
        >
          Annuler
        </Button>
      </TopHeader>

      <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:py-8 max-w-[1160px] mx-auto page-enter">
        {/* Heading */}
        <div className="mb-6 sm:mb-8">
          <SectionLabel>Nouvelle analyse</SectionLabel>
          <h1 className="mt-2 text-[24px] sm:text-[34px] font-semibold tracking-tight">
            Un bien. Trois étapes.{" "}
            <span className="font-serif italic font-normal text-ink-500">
              Soixante secondes.
            </span>
          </h1>
        </div>

        {/* Quick-import card — upload a PDF (annonce, mandat, compromis) and
            let Claude prefill the address + existant fields. Optional shortcut. */}
        <div className="mb-6">
          <ImportListingCard
            onExtracted={applyExtraction}
            requireAuth={() => {
              if (user) return true;
              setSignInOpen(true);
              return false;
            }}
            hasResult={!!extraction}
            extractedAddress={extraction?.address ?? null}
          />
        </div>

        {/* Stepper */}
        <div className="mb-8">
          <Stepper
            step={step}
            onGo={(n) => n <= step + 0 && setStep(n)}
          />
        </div>

        {/* Step 1 — collapses to a one-line summary when step >= 2.
            Click the header (or the Modifier button) to expand. */}
        <Card
          className={`${isStepExpanded(1) ? "p-7" : "py-4 px-7 cursor-pointer hover:bg-ink-50/40 transition"}`}
          onClick={
            !isStepExpanded(1)
              ? () => setExpandedSteps((s) => ({ ...s, 1: true }))
              : undefined
          }
        >
          <div
            className={`flex items-center justify-between ${isStepExpanded(1) ? "mb-5" : ""}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Pill tone={step >= 1 ? "lime" : "soft"}>Étape 1</Pill>
                <span className="text-[15px] font-semibold tracking-tight">
                  Adresse du bien
                </span>
                {!isStepExpanded(1) && addr && (
                  <span className="text-[13px] text-ink-700 truncate">
                    · {addr}
                  </span>
                )}
              </div>
              {isStepExpanded(1) && (
                <div className="text-[12.5px] text-ink-500 mt-1">
                  Tapez l’adresse exacte — la Base Adresse Nationale (BAN)
                  s’en charge au moment de la génération.
                </div>
              )}
            </div>
            {isStepExpanded(1) ? (
              addrLooksOk && (
                <Pill tone="green" icon={<Ic.Check size={11} />}>
                  Adresse renseignée
                </Pill>
              )
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedSteps((s) => ({ ...s, 1: true }));
                }}
                className="text-[12px] text-ink-700 hover:text-ink-900 underline shrink-0"
              >
                Modifier
              </button>
            )}
          </div>

          {isStepExpanded(1) && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left: input + suggestions + info */}
            <div>
              <div className="relative">
                <div className="bg-paper hairline rounded-xl2 flex items-center gap-3 px-4 h-14 focus-within:ring-2 focus-within:ring-lime-300">
                  <Ic.Pin size={16} className="text-ink-500" />
                  <input
                    autoFocus
                    value={addr}
                    onChange={(e) => {
                      setAddr(e.target.value);
                      // Any manual edit invalidates the picked geocode — the
                      // text no longer corresponds to a validated BAN row.
                      if (pickedGeocode) setPickedGeocode(null);
                    }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 150)}
                    className="flex-1 bg-transparent outline-none text-[14.5px] placeholder:text-ink-400"
                    placeholder="Ex : 12 rue des Lilas, 92100 Boulogne-Billancourt"
                  />
                  {banState.status === "loading" && (
                    <Ic.Bolt size={11} className="text-ink-400 animate-pulse" />
                  )}
                  <span className="text-[11px] font-mono text-ink-400">
                    BAN
                  </span>
                </div>
                {focused && !addrMatchesPick && banState.suggestions.length > 0 && (
                  <div className="absolute z-10 mt-2 left-0 right-0 bg-paper hairline rounded-xl2 shadow-pop overflow-hidden">
                    {banState.suggestions.map((s) => (
                      <button
                        key={`${s.label}|${s.lat}|${s.lng}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setAddr(s.label);
                          setPickedGeocode(suggestionToGeocodeResult(s));
                          setFocused(false);
                        }}
                        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-ink-50 border-t border-ink-200/70 first:border-0"
                      >
                        <Ic.Pin size={14} className="text-ink-500" />
                        <span className="text-[13px] flex-1 min-w-0 truncate">
                          {s.label}
                        </span>
                        <BanTypeChip type={s.type} />
                      </button>
                    ))}
                  </div>
                )}
                {focused &&
                  !addrMatchesPick &&
                  banState.status === "ready" &&
                  banState.suggestions.length === 0 &&
                  addr.trim().length >= 3 && (
                    <div className="absolute z-10 mt-2 left-0 right-0 bg-paper hairline rounded-xl2 shadow-pop overflow-hidden px-4 py-3 text-[12.5px] text-ink-500">
                      Aucune adresse ne correspond — vérifiez l'orthographe.
                    </div>
                  )}
                {pickedGeocode && addrMatchesPick && (
                  <div className="mt-2 flex items-center gap-2 text-[11.5px] text-ink-500">
                    <Ic.Check size={11} className="text-forest" />
                    <span className="tabular">
                      {pickedGeocode.lat.toFixed(5)},{" "}
                      {pickedGeocode.lng.toFixed(5)}
                    </span>
                    <span className="text-ink-400">·</span>
                    <span>INSEE {pickedGeocode.insee}</span>
                    <span className="text-ink-400">·</span>
                    <span>
                      score {(pickedGeocode.score * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
              </div>

              {/* Hint info — kept general since we don't know the zone yet. */}
              {addrLooksOk && (
                <div className="mt-5 grid gap-3">
                  <Card className="bg-lime-100 !shadow-none p-5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-ink-900 text-lime-300 grid place-items-center">
                        <Ic.Layers size={14} />
                      </div>
                      <div className="text-[12.5px] text-ink-700 font-medium">
                        Pipeline Arpentia — 4 étapes
                      </div>
                    </div>
                    <ul className="mt-3 space-y-1.5 text-[13px] text-ink-800">
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-ink-900" />{" "}
                        Géocodage BAN → parcelle IGN
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-ink-900" />{" "}
                        Détection zone PLU (Géoportail de l’urbanisme)
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-ink-900" />{" "}
                        Extraction du règlement applicable
                      </li>
                    </ul>
                  </Card>
                </div>
              )}

              {addrLooksOk && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-[12px] text-ink-500 flex items-center gap-2">
                    <Ic.Clock size={12} /> Temps estimé : ~30–60 s
                  </div>
                  <Button
                    variant="dark"
                    iconRight={<Ic.Arrow size={14} />}
                    onClick={() => setStep(2)}
                  >
                    Continuer vers l’existant
                  </Button>
                </div>
              )}
            </div>

            {/* Right: cadastre preview */}
            <div>
              <Cadastre className="h-[360px]" highlight={addrLooksOk} />
              <div className="mt-3 text-[11.5px] text-ink-500">
                Aperçu illustratif. La parcelle réelle et sa surface seront
                récupérées depuis le cadastre lors de la génération.
              </div>
            </div>
          </div>
          )}
        </Card>

        {/* Step 2 — collapses to a one-line summary when step >= 3. */}
        <Card
          className={`mt-4 ${
            step < 2
              ? "p-7 opacity-60 pointer-events-none"
              : isStepExpanded(2)
                ? "p-7"
                : "py-4 px-7 cursor-pointer hover:bg-ink-50/40 transition"
          }`}
          onClick={
            step >= 2 && !isStepExpanded(2)
              ? () => setExpandedSteps((s) => ({ ...s, 2: true }))
              : undefined
          }
        >
          <div
            className={`flex items-center justify-between ${isStepExpanded(2) || step < 2 ? "mb-5" : ""}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Pill tone={step >= 2 ? "lime" : "soft"}>Étape 2</Pill>
                <span className="text-[15px] font-semibold tracking-tight">
                  Caractéristiques de l’existant
                </span>
                {step >= 3 && !isStepExpanded(2) && (
                  <span className="text-[13px] text-ink-700 truncate">
                    · {existingSummary(existing)}
                  </span>
                )}
              </div>
              {(isStepExpanded(2) || step < 2) && (
                <div className="text-[12.5px] text-ink-500 mt-1">
                  Renseignez ce que vous connaissez du bien. Tous les champs sont
                  facultatifs.
                </div>
              )}
            </div>
            {step >= 3 && !isStepExpanded(2) ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedSteps((s) => ({ ...s, 2: true }));
                }}
                className="text-[12px] text-ink-700 hover:text-ink-900 underline shrink-0"
              >
                Modifier
              </button>
            ) : (
            <div className="flex items-center gap-1.5">
              {(["Maison", "Immeuble", "Terrain"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => patch("type", t)}
                  className={`text-[12px] px-3 h-8 rounded-full border transition ${
                    existing.type === t
                      ? "bg-ink-900 text-paper border-ink-900"
                      : "bg-paper border-ink-200 text-ink-700 hover:bg-ink-50"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            )}
          </div>

          {(isStepExpanded(2) || step < 2) && (
            <>
          {extraction && (
            <ExtractionSummary
              extraction={extraction}
              onApplyAddress={() => {
                if (extraction.address) setAddr(extraction.address);
              }}
              addressMatchesStep1={
                !!extraction.address &&
                addr.trim() === extraction.address.trim()
              }
            />
          )}

          <div className="grid md:grid-cols-4 gap-3">
            <FieldBlock
              label="Surface habitable"
              value={existing.surface_habitable_m2}
              onChange={(v) => patch("surface_habitable_m2", v)}
              unit="m²"
              icon={<Ic.Home size={14} />}
              placeholder="145"
              prefilled={prefilledFields.has("surface_habitable_m2")}
            />
            <FieldBlock
              label="Emprise au sol"
              value={existing.emprise_m2}
              onChange={(v) => patch("emprise_m2", v)}
              unit="m²"
              icon={<Ic.Grid size={14} />}
              placeholder="96"
              prefilled={prefilledFields.has("emprise_m2")}
            />
            <FieldBlock
              label="Hauteur actuelle"
              value={existing.hauteur_m}
              onChange={(v) => patch("hauteur_m", v)}
              unit="m"
              icon={<Ic.Ruler size={14} />}
              placeholder="8,2"
              prefilled={prefilledFields.has("hauteur_m")}
            />
            <FieldBlock
              label="Nombre de niveaux"
              value={existing.niveaux}
              onChange={(v) => patch("niveaux", v)}
              unit=""
              icon={<Ic.Building size={14} />}
              placeholder="R+1"
              prefilled={prefilledFields.has("niveaux")}
            />
          </div>

          <div className="mt-3 grid md:grid-cols-4 gap-3">
            <FieldBlock
              label="Année de construction"
              value={existing.year_built}
              onChange={(v) => patch("year_built", v)}
              unit=""
              icon={<Ic.Clock size={14} />}
              placeholder="1962"
              prefilled={prefilledFields.has("year_built")}
            />
          </div>

          <div className="mt-4 grid md:grid-cols-3 gap-3">
            <ToggleField
              label="Extension déjà déposée"
              on={existing.extension_deposee}
              onChange={(v) => patch("extension_deposee", v)}
            />
            <ToggleField
              label="Monument historique à proximité"
              on={existing.monument_proximite}
              onChange={(v) => patch("monument_proximite", v)}
            />
            <ToggleField
              label="Jardin arboré classé"
              on={existing.jardin_classe}
              onChange={(v) => patch("jardin_classe", v)}
            />
          </div>

          {step >= 2 && (
            <div className="mt-6 flex items-center justify-between">
              <Button
                variant="ghost"
                icon={<Ic.Arrow size={14} className="rotate-180" />}
                onClick={() => setStep(1)}
              >
                Retour
              </Button>
              <Button
                variant="dark"
                iconRight={<Ic.Arrow size={14} />}
                onClick={() => {
                  if (!user) {
                    setSignInOpen(true);
                    return;
                  }
                  // Pré-check : si on sait déjà (via le hook entitlements)
                  // qu'il n'y a plus de crédit, on ouvre la modale d'upsell
                  // sans passer en step 3. Évite de monter GenerationPanel
                  // pour le démonter aussitôt après le refus serveur.
                  if (
                    !entitlements.loading &&
                    !entitlements.canCreateFiche
                  ) {
                    setGatingModal(
                      entitlements.gatingReason ??
                        "no-subscription-no-credits",
                    );
                    return;
                  }
                  setStep(3);
                }}
              >
                Générer la fiche
              </Button>
            </div>
          )}
            </>
          )}
        </Card>

        {/* Step 3 */}
        <Card
          className={`mt-4 p-7 ${
            step >= 3 ? "" : "opacity-60 pointer-events-none"
          }`}
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Pill tone={step >= 3 ? "lime" : "soft"}>Étape 3</Pill>
              <span className="text-[15px] font-semibold tracking-tight">
                Génération de la fiche
              </span>
            </div>
          </div>

          {step < 3 ? (
            <div className="h-[180px] grid place-items-center">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto">
                  <Ic.Wand size={18} className="text-ink-500" />
                </div>
                <div className="mt-3 text-[13px] text-ink-500">
                  En attente de l’étape précédente…
                </div>
              </div>
            </div>
          ) : (
            <GenerationPanel
              address={addr}
              existing={toExistingProperty(existing)}
              cadastreRef={extraction?.cadastre ?? null}
              prefetchedGeocode={addrMatchesPick ? pickedGeocode : null}
              onDone={(ficheId) => go("fiche", { ficheId })}
              onCreditsExhausted={(reason) => {
                setGatingModal(reason);
                // Retour explicite à l'étape 2 : inutile de laisser la carte
                // step 3 montée en "idle" tant que la modale bloque l'UI.
                setStep(2);
              }}
            />
          )}
        </Card>
      </div>

      <SignInDialog
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        onSuccess={() => {
          setSignInOpen(false);
          setStep(3);
        }}
      />

      <CreditsExhaustedModal
        reason={gatingModal}
        onClose={() => setGatingModal(null)}
        onGoToPricing={(anchor) => {
          setGatingModal(null);
          // L'ancrage `#credits` (ou `#packs`) est consommé par Pricing au
          // mount : l'effet y lit `window.location.hash`, ouvre la section
          // (si repliée) et smooth-scrolle. On pose le hash AVANT `go()`
          // pour qu'il soit déjà en place quand la page remonte.
          if (anchor) {
            try {
              history.replaceState(null, "", `#${anchor}`);
            } catch {
              window.location.hash = anchor;
            }
          }
          go("pricing");
        }}
      />
    </AppShell>
  );
}

/* ---------- bits ---------- */

// Small badge on BAN suggestion rows — warns when a match is street-level
// (not a specific housenumber) so the user knows the parcel lookup will be
// less precise.
const BanTypeChip = ({ type }: { type: string }) => {
  const label: Record<string, string> = {
    housenumber: "N°",
    street: "Rue",
    locality: "Lieu-dit",
    municipality: "Commune",
  };
  const tone =
    type === "housenumber"
      ? "bg-lime-200 text-ink-900"
      : type === "street"
        ? "bg-amber-100 text-amber-900"
        : "bg-ink-100 text-ink-600";
  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-md shrink-0 ${tone}`}
    >
      {label[type] ?? type.slice(0, 4)}
    </span>
  );
};

const Stepper = ({
  step,
  onGo,
}: {
  step: number;
  onGo: (n: number) => void;
}) => {
  const steps = [
    { n: 1, label: "Adresse", icon: <Ic.Pin size={14} /> },
    { n: 2, label: "Existant", icon: <Ic.Home size={14} /> },
    { n: 3, label: "Génération", icon: <Ic.Wand size={14} /> },
  ];
  return (
    <div className="flex items-center gap-3">
      {steps.map((s, i) => (
        <Fragment key={s.n}>
          <button
            onClick={() => onGo(s.n)}
            className="flex items-center gap-3 group"
          >
            <span
              className={`w-9 h-9 rounded-full grid place-items-center transition
                ${
                  step === s.n
                    ? "bg-lime-400 text-ink-900 shadow-[inset_0_-2px_0_rgba(0,0,0,0.06)]"
                    : step > s.n
                      ? "bg-ink-900 text-lime-300"
                      : "bg-paper hairline text-ink-500"
                }`}
            >
              {step > s.n ? <Ic.Check size={14} /> : s.icon}
            </span>
            <span
              className={`text-[13.5px] ${
                step === s.n
                  ? "font-semibold text-ink-900"
                  : "text-ink-600"
              }`}
            >
              <span className="font-mono text-[10.5px] text-ink-400 mr-1.5">
                0{s.n}
              </span>
              {s.label}
            </span>
          </button>
          {i < steps.length - 1 && (
            <div className="flex-1 h-px bg-ink-200 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-ink-900 transition-all"
                style={{ width: step > s.n ? "100%" : "0%" }}
              />
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
};

const FieldBlock = ({
  label,
  value,
  onChange,
  unit,
  icon,
  placeholder,
  prefilled = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  unit: string;
  icon: ReactNode;
  placeholder?: string;
  prefilled?: boolean;
}) => (
  <label
    className={`rounded-xl2 p-4 block transition ${
      prefilled
        ? "bg-lime-100 ring-1 ring-lime-400/60"
        : "bg-ink-50"
    }`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-ink-500">
        {icon}
        <span className="text-[11.5px]">{label}</span>
      </div>
      {prefilled && (
        <span className="text-[10px] font-mono uppercase tracking-widest text-ink-700">
          auto
        </span>
      )}
    </div>
    <div className="mt-1 flex items-baseline gap-1">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent outline-none text-[26px] font-semibold tracking-tight tabular placeholder:text-ink-300"
      />
      {unit && <span className="text-[13px] text-ink-500">{unit}</span>}
    </div>
    <div className="mt-2 h-px bg-ink-200" />
    <div className="mt-2 text-[11px] text-ink-500">
      {prefilled
        ? "Prérempli depuis le document — modifiable"
        : "Optionnel — affiné par le cadastre"}
    </div>
  </label>
);

/* ---------- PDF import: upload + extract + prefill ---------- */

const ImportListingCard = ({
  onExtracted,
  requireAuth,
  hasResult,
  extractedAddress,
}: {
  onExtracted: (ex: ListingExtraction) => void;
  requireAuth: () => boolean;
  hasResult: boolean;
  extractedAddress: string | null;
}) => {
  const [status, setStatus] = useState<"idle" | "running" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function handleFile(file: File) {
    if (!requireAuth()) return;
    setStatus("running");
    setError(null);
    setFilename(file.name);
    setElapsedMs(null);
    const t0 = performance.now();
    try {
      const ex = await extractListingFromPdf(file);
      setElapsedMs(performance.now() - t0);
      setStatus("idle");
      onExtracted(ex);
    } catch (e) {
      const msg =
        e instanceof ListingExtractionError
          ? e.message
          : e instanceof Error
            ? e.message
            : String(e);
      setError(msg);
      setStatus("error");
    }
  }

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
    // Reset so re-selecting the same file fires onChange again.
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <Card className="p-5 !bg-ink-900 text-paper !shadow-none">
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={onChange}
        className="hidden"
      />
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-lime-400 text-ink-900 grid place-items-center shrink-0">
          <Ic.File size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono uppercase tracking-widest text-lime-300">
              Raccourci
            </span>
            <Pill tone="lime" className="!bg-lime-400/90">
              IA
            </Pill>
          </div>
          <div className="mt-1 text-[14.5px] font-semibold tracking-tight">
            Importer une annonce ou un compromis (PDF)
          </div>
          <div className="mt-0.5 text-[12.5px] text-paper/70">
            {status === "running" ? (
              <>
                Analyse de « {filename} » en cours — l'IA lit le document…
              </>
            ) : status === "error" ? (
              <span className="text-rose-300">{error}</span>
            ) : hasResult ? (
              <>
                Document analysé
                {elapsedMs
                  ? ` en ${(elapsedMs / 1000).toFixed(1)} s`
                  : ""}
                {extractedAddress ? ` · ${extractedAddress}` : ""} — vous
                pouvez en charger un autre à tout moment.
              </>
            ) : (
              <>
                Glissez un PDF ou cliquez ci-contre — l’IA remplira
                l’adresse et les caractéristiques du bien.
              </>
            )}
          </div>
        </div>
        <Button
          variant="primary"
          size="sm"
          icon={
            status === "running" ? (
              <Ic.Bolt size={13} className="animate-pulse" />
            ) : (
              <Ic.Wand size={13} />
            )
          }
          onClick={() => inputRef.current?.click()}
          disabled={status === "running"}
        >
          {status === "running"
            ? "Analyse…"
            : hasResult
              ? "Remplacer"
              : "Choisir un PDF"}
        </Button>
      </div>
    </Card>
  );
};

/* ---------- Extraction summary panel inside Step 2 ---------- */

const ExtractionSummary = ({
  extraction,
  onApplyAddress,
  addressMatchesStep1,
}: {
  extraction: ListingExtraction;
  onApplyAddress: () => void;
  addressMatchesStep1: boolean;
}) => (
  <div className="mb-5 grid md:grid-cols-[1.2fr_1fr] gap-3">
    <Card className="p-4 bg-lime-100 !shadow-none">
      <div className="flex items-center gap-2">
        <Ic.Sparkle size={14} />
        <span className="text-[11px] font-mono uppercase tracking-widest text-ink-700">
          Analyse du document
        </span>
      </div>
      <div className="mt-2 text-[13px] text-ink-800 leading-relaxed">
        {extraction.summary}
      </div>
      {extraction.address && (
        <div className="mt-3 flex items-center gap-2 text-[12px]">
          <Ic.Pin size={12} className="text-ink-500" />
          <span className="text-ink-700 flex-1 truncate">
            {extraction.address}
          </span>
          {addressMatchesStep1 ? (
            <Pill tone="green" icon={<Ic.Check size={10} />}>
              Adresse utilisée
            </Pill>
          ) : (
            <button
              onClick={onApplyAddress}
              className="text-[11.5px] font-medium text-ink-900 underline underline-offset-2 hover:no-underline"
            >
              Utiliser pour l’étape 1
            </button>
          )}
        </div>
      )}
      {extraction.notes.length > 0 && (
        <ul className="mt-3 space-y-1">
          {extraction.notes.map((n, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-[12px] text-ink-700"
            >
              <span className="mt-[7px] w-1 h-1 rounded-full bg-ink-900 shrink-0" />
              <span>{n}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>

    <Card className="p-4 bg-amber-50 !shadow-none border border-amber-200">
      <div className="flex items-center gap-2">
        <Ic.Bell size={14} className="text-amber-700" />
        <span className="text-[11px] font-mono uppercase tracking-widest text-amber-800">
          {extraction.questions.length} question
          {extraction.questions.length > 1 ? "s" : ""} à poser
        </span>
      </div>
      {extraction.questions.length === 0 ? (
        <div className="mt-2 text-[12.5px] text-amber-900/80">
          Toutes les informations utiles ont été trouvées dans le document.
        </div>
      ) : (
        <ul className="mt-2 space-y-2">
          {extraction.questions.map((q, i) => (
            <li key={i} className="text-[12.5px] text-ink-800">
              <span className="font-medium">{q.question}</span>
              {q.hint && (
                <span className="block text-[11.5px] text-ink-500 mt-0.5">
                  {q.hint}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  </div>
);

const ToggleField = ({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (v: boolean) => void;
}) => (
  <button
    type="button"
    onClick={() => onChange(!on)}
    className="w-full bg-ink-50 rounded-xl2 p-4 flex items-center justify-between gap-3 text-left"
  >
    <span className="text-[13px] text-ink-800">{label}</span>
    <span
      className={`w-9 h-5 rounded-full relative transition ${
        on ? "bg-lime-400" : "bg-ink-300"
      }`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 rounded-full bg-paper shadow transition-all ${
          on ? "left-[18px]" : "left-0.5"
        }`}
      />
    </span>
  </button>
);

/* ---------- Real pipeline panel ---------- */

type StageUIState = {
  key: StageKey;
  label: string;
  status: StageStatus;
  message?: string;
  /** performance.now() when the stage transitioned to "running" — used to
   *  render a live chrono while the stage is still in flight. */
  startedAt?: number;
  /** elapsed ms when stage finished — set on done/error */
  elapsedMs?: number;
  /** Optional UX flag for "the call succeeded but the result is degenerate
   *  and worth a second look" (e.g. BD TOPO returned 0 buildings — could be
   *  a real terrain nu, or a stale dataset). Renders the row with an amber
   *  attention indicator instead of the green checkmark. */
  attention?: boolean;
};

const STAGE_LABELS: Record<StageKey, string> = {
  geocode: "Géocodage de l’adresse (BAN)",
  cadastre: "Récupération de la parcelle cadastrale (IGN)",
  footprint: "Mesure de l’empreinte bâtie (BD TOPO)",
  zone: "Détection de la zone PLU (GPU)",
  reglement: "Extraction du règlement applicable",
};

function initialStages(): StageUIState[] {
  return (Object.keys(STAGE_LABELS) as StageKey[]).map((key) => ({
    key,
    label: STAGE_LABELS[key],
    status: "pending",
  }));
}

const GenerationPanel = ({
  address,
  existing,
  cadastreRef,
  prefetchedGeocode,
  onDone,
  onCreditsExhausted,
}: {
  address: string;
  existing: ExistingProperty;
  cadastreRef: {
    section: string | null;
    numero: string | null;
    prefixe: string | null;
  } | null;
  prefetchedGeocode: GeocodeResult | null;
  onDone: (ficheId: string) => void;
  /** Déclenché quand `consumeCreditForFiche` refuse — le parent ouvre la
   *  modale d'upsell. Le GenerationPanel reste dans un état "bloqué" jusqu'à
   *  ce que l'utilisateur soit redirigé ou ferme la modale. */
  onCreditsExhausted: (reason: GatingReason) => void;
}) => {
  const { user } = useAuth();
  const [stages, setStages] = useState<StageUIState[]>(initialStages);
  const [status, setStatus] = useState<
    "idle" | "running" | "error" | "done"
  >("idle");
  const [error, setError] = useState<{
    stage: StageKey;
    code: string;
    message: string;
  } | null>(null);
  // Cumulative pipeline result. Filled progressively via runPipeline's
  // onPartialResult callback so that, when a late stage fails (e.g. zone),
  // we still have geocode + cadastre to power the manual-PLU upload modal.
  const [result, setResult] = useState<PipelineResult | null>(null);
  const [partialResult, setPartialResult] = useState<Partial<PipelineResult>>(
    {},
  );
  // Pre-allocated id used both for the upload modal's Storage path and for
  // the eventual createFiche call — keeps the Storage object owned by the
  // fiche it ultimately belongs to.
  const pendingFicheIdRef = useRef<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Mid-pipeline parcel disambiguation. When cadastre returns multiple
  // candidates, pipeline pauses on the resolver — the picker modal
  // resolves with the chosen parcel (or rejects on cancel, which we
  // treat as silent abort + refund).
  const [parcelChoice, setParcelChoice] = useState<{
    candidates: CadastreParcel[];
    autoPick: CadastreParcel;
    resolve: (p: CadastreParcel) => void;
    reject: (e: PipelineError) => void;
  } | null>(null);
  // Set when the user already explicitly picked a parcel mid-pipeline.
  // Used to hide the inline post-pipeline picker (it's redundant — the
  // agent just made the choice). They can still adjust via a "Modifier
  // la parcelle" button on the result.
  const [userPickedMidPipeline, setUserPickedMidPipeline] = useState(false);
  // Refund state surfaced by the error modal when the pipeline throws after a
  // successful consume. "skipped" = no consume happened yet (modal not shown).
  const [refundState, setRefundState] = useState<RefundState>("skipped");
  // Refund-guard: the token stored here is the one we got back from the most
  // recent consume. Cleared as soon as we refund it so a second failure in the
  // same session can't cause a double-refund.
  const refundTokenRef = useRef<string | null>(null);
  // Tracks the parcel the user currently has selected. Starts at the auto-pick
  // from the pipeline, can be switched by the user when `parcelAlternatives`
  // is non-empty. Persisted back into the fiche on every change.
  //
  // We hold the full `CadastreParcel` (not just the id) because the user can
  // pick a MULTI-parcel composite via the map picker, and the composite
  // isn't present in `result.parcelAlternatives` — a lookup by id would miss
  // it and the UI would silently fall back to the original auto-pick.
  const [selectedParcel, setSelectedParcel] = useState<CadastreParcel | null>(
    null,
  );
  const [savedFicheId, setSavedFicheId] = useState<string | null>(null);
  const [totalMs, setTotalMs] = useState<number | null>(null);

  // Auto-start once when the panel mounts.
  //
  // React 18 StrictMode deliberately double-invokes mount effects in dev
  // (mount → cleanup → mount) with the SAME closure, so the `status === "idle"`
  // guard doesn't help — both runs see the initial "idle" state and both fire
  // `start()`, which calls `consumeCreditForFiche` twice and charges the user
  // two credits for one analysis. A ref is the only thing that survives the
  // cleanup/remount cycle intact, so we use it as the real single-fire gate.
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    if (status === "idle") void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tick a "now" value every 500 ms while the pipeline is running so the
  // chrono on the active stage ticks up without each stage tracking its own
  // interval. 500 ms feels smooth enough for a seconds-level display and
  // cheap enough (one state update / two renders per second) even with the
  // component tree we have. Cleaned up as soon as the run finishes.
  const [tickNow, setTickNow] = useState(() => performance.now());
  useEffect(() => {
    if (status !== "running") return;
    const id = window.setInterval(() => setTickNow(performance.now()), 500);
    return () => window.clearInterval(id);
  }, [status]);

  async function start() {
    setStatus("running");
    setError(null);
    setStages(initialStages());
    setResult(null);
    setPartialResult({});
    setSelectedParcel(null);
    setSavedFicheId(null);
    setTotalMs(null);
    // Pre-allocate the fiche id so the manual-PLU upload (if needed) can land
    // at users/{uid}/fiches/{id}/pluUploads/... and the eventual createFiche
    // can reuse the same id. We mint a fresh one on every retry.
    pendingFicheIdRef.current = newFicheId();
    setUserPickedMidPipeline(false);
    // Refund lifecycle resets at the top of every attempt. If a previous
    // attempt refunded, `refundTokenRef.current` is already null — the new
    // consume below will populate it again.
    setRefundState("skipped");

    // ─── Gate : consommer 1 crédit AVANT le pipeline ───────────────────
    // La Cloud Function `consumeCreditForFiche` décrémente atomiquement le
    // crédit le plus approprié (free → top-up → quota mensuel). Refus =>
    // on affiche la modale d'upsell et on arrête ici. Le pipeline coûte
    // jusqu'à ~1,11 $ en cache-miss, hors de question de le lancer avant
    // d'être sûr que l'utilisateur a le droit.
    const consume = await consumeCreditForFiche();
    if (!consume.ok) {
      setStatus("idle");
      onCreditsExhausted(
        consume.code === "subscription-inactive"
          ? "subscription-past-due"
          : "no-subscription-no-credits",
      );
      return;
    }
    // Stash the refund token so the error handler below can reverse the
    // consume if the pipeline throws. The ref (not state) survives the
    // React re-render from `setStatus("running")` and is easy to null out
    // after a successful refund to prevent double-calls.
    refundTokenRef.current = consume.refundToken;

    const t0 = performance.now();
    const stageStart: Partial<Record<StageKey, number>> = {};

    const onProgress = (e: StageEvent) => {
      if (e.status === "running") stageStart[e.stage] = performance.now();
      setStages((prev) =>
        prev.map((s) =>
          s.key === e.stage
            ? {
                ...s,
                status: e.status,
                message: e.message,
                attention: e.attention,
                startedAt:
                  e.status === "running" ? stageStart[e.stage] : s.startedAt,
                elapsedMs:
                  e.status === "done" || e.status === "error"
                    ? performance.now() - (stageStart[e.stage] ?? t0)
                    : s.elapsedMs,
              }
            : s,
        ),
      );
    };

    try {
      const res = await runPipeline({
        address,
        onProgress,
        cadastreRef,
        prefetchedGeocode,
        onPartialResult: (partial) => setPartialResult(partial),
        onParcelChoice: ({ candidates, autoPick }) =>
          new Promise<CadastreParcel>((resolve, reject) => {
            setParcelChoice({ candidates, autoPick, resolve, reject });
          }),
      });
      setResult(res);
      setSelectedParcel(res.parcel);
      // Persist fiche to Firestore — this is what the Fiche page will read.
      // We save with the auto-picked parcel immediately; if the user later
      // switches to an alternative (picker in ResultPreview), we update the
      // fiche in place via updateFicheParcel().
      // Pre-fill emprise/hauteur from BD TOPO when the agent didn't provide
      // values — the manual input always takes priority, BD TOPO only fills
      // what's missing. Keeps Hero (emprise résiduelle) aligned with the AI
      // potentiel card, which reads BD TOPO directly.
      const mergedExisting = mergeFootprintIntoExisting(existing, res.footprint);
      const fiche = await createFiche({
        id: pendingFicheIdRef.current ?? undefined,
        geocode: res.geocode,
        parcel: res.parcel,
        zone: res.zone,
        reglement: res.reglement,
        existing: mergedExisting,
      });
      setSavedFicheId(fiche.id);
      setTotalMs(performance.now() - t0);
      setStatus("done");
      // Pipeline succeeded — the credit is legitimately spent. Drop the
      // token so nothing can refund it later (e.g. a retry flow).
      refundTokenRef.current = null;
    } catch (e) {
      const pe =
        e instanceof PipelineError
          ? e
          : new PipelineError({
              code: "unknown",
              stage: "geocode",
              message: e instanceof Error ? e.message : String(e),
            });

      // ─── User-cancelled (e.g. closed mid-pipeline picker) ─────────
      // Silent path : refund, clear state, NO error modal — the user
      // explicitly aborted, surfacing an error UI would feel punitive.
      if (pe.code === "cancelled-by-user") {
        setStatus("idle");
        setStages(initialStages());
        setResult(null);
        setPartialResult({});
        const token = refundTokenRef.current;
        if (token) {
          refundTokenRef.current = null;
          await refundCreditForFiche(token);
        }
        return;
      }

      // Mark the failing stage as errored if it wasn't already.
      setStages((prev) =>
        prev.map((s) =>
          s.key === pe.stage
            ? { ...s, status: "error", message: pe.message }
            : s,
        ),
      );
      setError({ stage: pe.stage, code: pe.code, message: pe.message });
      setStatus("error");

      // Refund the credit consumed up-front. Guarded on the ref so retries
      // and StrictMode double-effect-runs can't double-credit the user.
      const token = refundTokenRef.current;
      if (token) {
        refundTokenRef.current = null;
        setRefundState("pending");
        const refund = await refundCreditForFiche(token);
        setRefundState(refund.ok ? "refunded" : "failed");
      } else {
        setRefundState("skipped");
      }
    }
  }

  // ─── Manual-PLU upload fallback ──────────────────────────────────────
  // User picked "Uploader le règlement et le plan" from the error modal.
  // Once they confirm a zone in PluUploadModal, we re-consume a credit
  // (a new attempt = a new charge), run extractReglement on their PDF,
  // and persist the fiche under the same pre-allocated id used by the
  // upload's Storage path.
  async function handleUploadConfirmed(args: { zoneCode: string }) {
    setUploadOpen(false);

    if (!user || !pendingFicheIdRef.current || !partialResult.geocode || !partialResult.parcel) {
      // Defensive: this should never happen — the CTA only opens when the
      // upload modal has the data it needs. Surface a clean error if it does.
      setError({
        stage: "reglement",
        code: "unknown",
        message: "État interne incohérent. Relancez l'analyse.",
      });
      setStatus("error");
      return;
    }

    const ficheId = pendingFicheIdRef.current;
    const geocode = partialResult.geocode;
    const parcel = partialResult.parcel;

    setStatus("running");
    const reglementStartTs = performance.now();
    setStages((prev) =>
      prev.map((s) => {
        if (s.key === "zone") {
          return {
            ...s,
            status: "done",
            message: `Zone ${args.zoneCode} (saisie manuelle)`,
            elapsedMs: 0,
          };
        }
        if (s.key === "reglement") {
          return {
            ...s,
            status: "running",
            message: "Extraction depuis votre upload…",
            startedAt: reglementStartTs,
            elapsedMs: undefined,
          };
        }
        return s;
      }),
    );

    // Re-consume — this is a new attempt and we already refunded the previous one.
    const consume = await consumeCreditForFiche();
    if (!consume.ok) {
      setStatus("idle");
      onCreditsExhausted(
        consume.code === "subscription-inactive"
          ? "subscription-past-due"
          : "no-subscription-no-credits",
      );
      return;
    }
    refundTokenRef.current = consume.refundToken;
    setRefundState("skipped");

    try {
      const reglement = await extractReglementFromUserUpload({
        uid: user.uid,
        ficheId,
        insee: geocode.insee,
        communeName: geocode.city,
        zoneCode: args.zoneCode,
      });

      // Build a synthetic PluZone — we don't have the GPU's libelong since
      // the user provided the document. We CAN derive `typezone` from the
      // zone code prefix (matches the GPU convention : U / AU / A / N).
      const syntheticZone = {
        libelle: args.zoneCode,
        libelong: `Zone ${args.zoneCode}`,
        typezone: deriveTypezoneFromCode(args.zoneCode),
        destdomi: null,
        partition: "",
        reglement_url: `storage://users/${user.uid}/fiches/${ficheId}/pluUploads/reglement.pdf`,
        commune_insee: geocode.insee,
        idurba: null,
        nomfic: null,
      };

      const mergedExisting = mergeFootprintIntoExisting(existing, partialResult.footprint ?? null);
      const fiche = await createFiche({
        id: ficheId,
        geocode,
        parcel,
        zone: syntheticZone,
        reglement,
        existing: mergedExisting,
      });
      setSavedFicheId(fiche.id);
      setResult({
        geocode,
        parcel,
        parcelAlternatives: partialResult.parcelAlternatives ?? [],
        footprint: partialResult.footprint ?? null,
        zone: syntheticZone,
        reglement,
      });
      setSelectedParcel(parcel);
      setStages((prev) =>
        prev.map((s) =>
          s.key === "zone"
            ? { ...s, status: "done", message: `Zone ${args.zoneCode} (saisie manuelle)` }
            : s.key === "reglement"
              ? { ...s, status: "done", message: `${reglement.raw_sections.length} sections extraites.` }
              : s,
        ),
      );
      setStatus("done");
      refundTokenRef.current = null;
    } catch (e) {
      // Surface the error in the console — the upload flow is new, easier to
      // debug if the failure mode is visible without dipping into Cloud Logs.
      console.error("handleUploadConfirmed failed", e);
      const message = e instanceof Error ? e.message : String(e);
      const code =
        (e as { details?: { code?: string } })?.details?.code ??
        (e as { code?: string })?.code ??
        "unknown";
      setError({ stage: "reglement", code: String(code), message });
      setStatus("error");
      const token = refundTokenRef.current;
      if (token) {
        refundTokenRef.current = null;
        setRefundState("pending");
        const refund = await refundCreditForFiche(token);
        setRefundState(refund.ok ? "refunded" : "failed");
      } else {
        setRefundState("skipped");
      }
    }
  }

  const canOpenUpload =
    status === "error" &&
    !!user &&
    !!pendingFicheIdRef.current &&
    !!partialResult.geocode &&
    !!partialResult.parcel;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <ul className="space-y-2">
          {stages.map((s) => (
            <StageRow key={s.key} stage={s} now={tickNow} />
          ))}
        </ul>

        {status === "error" && error && !uploadOpen && (
          <PipelineErrorModal
            stageLabel={STAGE_LABELS[error.stage]}
            errorMessage={error.message}
            errorCode={error.code}
            refundState={refundState}
            onRetry={() => {
              // The modal closes itself by flipping status back to "running"
              // via start(); no explicit close needed.
              void start();
            }}
            onClose={() => setStatus("idle")}
            onUploadFallback={
              canOpenUpload ? () => setUploadOpen(true) : undefined
            }
          />
        )}

        {uploadOpen &&
          user &&
          pendingFicheIdRef.current &&
          partialResult.geocode &&
          partialResult.parcel && (
            <PluUploadModal
              open
              uid={user.uid}
              ficheId={pendingFicheIdRef.current}
              expectedInsee={partialResult.geocode.insee}
              expectedCommuneName={partialResult.geocode.city}
              lat={partialResult.geocode.lat}
              lng={partialResult.geocode.lng}
              parcel={partialResult.parcel}
              onClose={() => setUploadOpen(false)}
              onZoneConfirmed={(args) => {
                void handleUploadConfirmed(args);
              }}
            />
          )}

        {/* Mid-pipeline parcel picker — cadastre returned several
            candidates and the pipeline is awaiting the user's choice.
            Re-uses the existing ParcelPickerMap (Leaflet, sat/plan
            toggle). Confirm resolves the resolver promise; cancel
            rejects with `cancelled-by-user` which the catch block
            handles silently (refund + reset, no error modal). */}
        {parcelChoice && (
          <ParcelPickerMap
            candidates={parcelChoice.candidates}
            initialSelectedIds={new Set([parcelChoice.autoPick.id])}
            onConfirm={(ids) => {
              const picked = parcelChoice.candidates.filter((c) =>
                ids.includes(c.id),
              );
              const final =
                picked.length === 1
                  ? picked[0]
                  : picked.length > 1
                    ? mergeParcels(picked)
                    : parcelChoice.autoPick;
              parcelChoice.resolve(final);
              setParcelChoice(null);
              setUserPickedMidPipeline(true);
            }}
            onCancel={() => {
              parcelChoice.reject(
                new PipelineError({
                  code: "cancelled-by-user",
                  stage: "cadastre",
                  message: "Sélection de parcelle annulée par l'utilisateur.",
                }),
              );
              setParcelChoice(null);
            }}
          />
        )}

        {status === "running" && !parcelChoice && (
          <div className="mt-5 text-[12px] text-ink-500">
            Analyse en cours. Ne fermez pas cette fenêtre.
          </div>
        )}
      </div>

      <div>
        <ResultPreview
          result={result}
          selectedParcel={selectedParcel}
          hideInlinePicker={userPickedMidPipeline}
          onPickParcel={async (p) => {
            // Optimistic UI: switch the highlighted parcel right away.
            setSelectedParcel(p);

            // Spin up the BD TOPO row again — the previous footprint was
            // tied to the auto-pick, this one belongs to a different
            // polygon. Set startedAt so the chrono ticks.
            setStages((prev) =>
              prev.map((s) =>
                s.key === "footprint"
                  ? {
                      ...s,
                      status: "running",
                      message: "Re-mesure pour la nouvelle parcelle…",
                      startedAt: performance.now(),
                      attention: undefined,
                      elapsedMs: undefined,
                    }
                  : s,
              ),
            );

            const newFootprint = await refetchFootprint(p);

            // Update the stage row with the new footprint outcome.
            setStages((prev) =>
              prev.map((s) => {
                if (s.key !== "footprint") return s;
                if (!newFootprint) {
                  return {
                    ...s,
                    status: "error",
                    message: "BD TOPO indisponible — mesure non rafraîchie.",
                  };
                }
                const noBuildings = newFootprint.buildingCount === 0;
                const heightSuffix =
                  newFootprint.maxHeight_m != null
                    ? ` · ${newFootprint.maxHeight_m} m`
                    : "";
                return {
                  ...s,
                  status: "done",
                  attention: noBuildings,
                  message: noBuildings
                    ? "Aucun bâtiment détecté — terrain nu ou bâti récent (à vérifier)"
                    : `${newFootprint.buildingCount} bâtiment${newFootprint.buildingCount > 1 ? "s" : ""} · ${newFootprint.builtFootprint_m2} m²${heightSuffix}`,
                  elapsedMs: undefined,
                };
              }),
            );

            // Reflect the new footprint in the cumulative result so any UI
            // reading `result.footprint` (Hero, Potentiel card) refreshes.
            setResult((r) => (r ? { ...r, parcel: p, footprint: newFootprint } : r));
            setPartialResult((pr) => ({ ...pr, parcel: p, footprint: newFootprint }));

            // Persist the new parcel + refreshed `existing` to Firestore.
            // BD TOPO-sourced fields follow the parcel; user-edited fields
            // are preserved (cf. refreshBdTopoFields).
            if (savedFicheId) {
              try {
                const refreshedExisting = refreshBdTopoFields(existing, newFootprint);
                await updateFicheParcelAndExisting(
                  savedFicheId,
                  p,
                  refreshedExisting,
                );
              } catch (err) {
                console.warn("updateFicheParcelAndExisting failed", err);
              }
            }
          }}
        />
      </div>

      {/* Footer "Analyse terminée" — pleine largeur sous le 2-col grid.
          Placé ici (grid child avec col-span-full) plutôt qu'enfermé dans
          la colonne de gauche : le CTA primaire "Ouvrir la fiche" est
          l'action logique suivante, il doit être visuellement dominant et
          proche de la parcelle/règlement qui viennent d'être générés. */}
      {status === "done" && savedFicheId && (
        <div className="lg:col-span-2 flex items-center justify-between gap-4 pt-5 border-t border-ink-200">
          <div className="text-[13px] text-ink-500">
            Terminé en{" "}
            <strong className="text-ink-900 font-semibold">
              {totalMs ? `${(totalMs / 1000).toFixed(1)} s` : "—"}
            </strong>
          </div>
          <Button
            variant="dark"
            size="lg"
            iconRight={<Ic.Arrow size={14} />}
            onClick={() => onDone(savedFicheId)}
          >
            Ouvrir la fiche
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Running hint for `reglement` — the only stage slow enough that the user
 * needs reassurance it's still working. Backend callables are unary so we
 * can't stream real sub-step progress; these are time-based estimates that
 * roughly match what the Cloud Function actually does (fetch PDF → filter
 * pages → Claude extraction). They're directionally honest: cache-hit runs
 * finish before any hint shows up, and filter/Claude timings are consistent
 * enough that a user glancing at this won't feel misled.
 */
function reglementSubStep(elapsedMs: number): string {
  if (elapsedMs < 3_000) return "Téléchargement du règlement…";
  if (elapsedMs < 12_000) return "Filtrage des pages pertinentes…";
  return "Analyse IA du règlement — peut prendre jusqu'à 2 minutes…";
}

const StageRow = ({
  stage,
  now,
}: {
  stage: StageUIState;
  /** `performance.now()` tick shared by the GenerationPanel — drives the
   *  chrono on the currently-running stage without each row owning its own
   *  interval. */
  now: number;
}) => {
  const tone =
    stage.status === "done" && stage.attention
      ? "bg-amber-400 text-ink-900"
      : stage.status === "done"
        ? "bg-forest text-white"
        : stage.status === "error"
          ? "bg-brick text-white"
          : stage.status === "running"
            ? "bg-ink-900 text-lime-300 animate-pulse"
            : "bg-ink-200 text-ink-500";

  // Live elapsed (running) vs. frozen elapsed (done/error).
  const liveMs =
    stage.status === "running" && stage.startedAt != null
      ? Math.max(0, now - stage.startedAt)
      : null;

  // Sub-step hint: only shown for reglement while running. Other stages are
  // short enough that an extra line would just add noise.
  const subHint =
    stage.status === "running" && stage.key === "reglement" && liveMs != null
      ? reglementSubStep(liveMs)
      : null;

  const chrono =
    stage.status === "pending"
      ? "—"
      : stage.status === "running" && liveMs != null
        ? `${(liveMs / 1000).toFixed(0)} s`
        : stage.elapsedMs
          ? `${(stage.elapsedMs / 1000).toFixed(1)} s`
          : "";

  return (
    <li className="flex items-center gap-3 bg-ink-50 rounded-lg px-4 py-3">
      <span
        className={`w-6 h-6 rounded-full grid place-items-center ${tone}`}
      >
        {stage.status === "done" && stage.attention ? (
          <Ic.AlertTriangle size={12} />
        ) : stage.status === "done" ? (
          <Ic.Check size={12} />
        ) : stage.status === "error" ? (
          <Ic.Cross size={12} />
        ) : (
          <span className="w-1.5 h-1.5 rounded-full bg-current" />
        )}
      </span>
      <span className="text-[13px] flex-1">
        <span className="block">{stage.label}</span>
        {(subHint ?? stage.message) && (
          <span className="block text-[11.5px] text-ink-500 mt-0.5">
            {subHint ?? stage.message}
          </span>
        )}
      </span>
      <span className="text-[11.5px] font-mono text-ink-500 tabular-nums">
        {chrono}
      </span>
    </li>
  );
};

// Threshold mirrored from functions/src/getCadastreParcel.ts — when the
// selected parcel is above this, show a "large parcel — verify" warning so
// the user thinks twice before saving. The threshold is intentionally loose
// to avoid false warnings on rural multi-hectare properties.
const LARGE_PARCEL_WARN_M2 = 15_000;

const ResultPreview = ({
  result,
  selectedParcel,
  onPickParcel,
  hideInlinePicker,
}: {
  result: PipelineResult | null;
  /** Currently-selected parcel — full object so MULTI-composites work.
   *  Null during the very first render before the pipeline completes. */
  selectedParcel: CadastreParcel | null;
  onPickParcel: (p: CadastreParcel) => void;
  /** When true, the verbose inline list of candidate parcels is collapsed
   *  to a single "Modifier la parcelle" button. Set when the agent already
   *  picked the right parcel(s) mid-pipeline — re-prompting here would be
   *  redundant friction. They can still adjust by opening the map picker. */
  hideInlinePicker?: boolean;
}) => {
  // Map-based parcel picker. Opens on demand from the inline list — we don't
  // auto-open it because the heuristic is correct often enough that forcing
  // a modal on every run would be noisy.
  const [mapOpen, setMapOpen] = useState(false);

  if (!result) {
    return (
      <Card className="p-5 bg-ink-50 !shadow-none h-full min-h-[240px] flex items-center justify-center">
        <div className="text-center text-ink-500 text-[12.5px] max-w-[240px]">
          Le résumé de la parcelle et du règlement apparaîtra ici une fois la
          génération terminée.
        </div>
      </Card>
    );
  }
  const { parcel, parcelAlternatives, zone, reglement } = result;
  // All candidates (best + alternatives), sorted by contenance asc — this is
  // the list the picker exposes. The currently-selected one gets highlighted.
  const allCandidates = [parcel, ...parcelAlternatives].sort(
    (a, b) => (a.surface_m2 || 0) - (b.surface_m2 || 0),
  );
  // Prefer the explicit selection (including MULTI-composites not in
  // allCandidates); fall back to the pipeline's auto-pick.
  const selected = selectedParcel ?? parcel;
  const isLarge = selected.surface_m2 > LARGE_PARCEL_WARN_M2;
  // Set of cadastre IDs the pickers should highlight. For a MULTI-parcel
  // composite this is the component IDs, so both the inline list and the
  // map modal mark every sub-parcel visibly (instead of looking "empty"
  // because no candidate id literally equals "MULTI:…+…").
  const selectedIdSet = new Set(parcelComponentIds(selected.id));

  const kv = (k: string, v: string | number | null) => (
    <div className="flex items-center justify-between py-1.5 border-b border-ink-200/70 last:border-0">
      <span className="text-[12px] text-ink-500">{k}</span>
      <span className="text-[12.5px] font-medium tabular">
        {v === null || v === "" ? "—" : v}
      </span>
    </div>
  );
  return (
    <Card className="p-5 bg-lime-200 !shadow-none">
      <div className="flex items-center gap-2">
        <Ic.Bolt size={14} />
        <span className="text-[11.5px] font-mono uppercase tracking-widest">
          Résultat
        </span>
      </div>
      <div className="mt-3 text-[20px] font-semibold tracking-tight">
        Zone {zone.libelle}
      </div>
      <div className="text-[12.5px] text-ink-700">{zone.libelong}</div>
      <div className="mt-4 grid grid-cols-1 gap-0 text-ink-800">
        {kv("Parcelle", parcelLabel(selected))}
        {kv("Surface parcelle", `${selected.surface_m2.toLocaleString("fr-FR")} m²`)}
        {kv(
          "Emprise max",
          reglement.emprise_au_sol_max_pct !== null
            ? `${reglement.emprise_au_sol_max_pct} %`
            : null,
        )}
        {kv(
          "Hauteur max",
          reglement.hauteur_max_m !== null
            ? `${reglement.hauteur_max_m} m`
            : reglement.hauteur_max_etages,
        )}
        {kv(
          "Retrait voirie",
          reglement.recul_voie_m !== null
            ? `${reglement.recul_voie_m} m`
            : null,
        )}
        {kv(
          "Retrait limites",
          reglement.recul_limites_m !== null
            ? `${reglement.recul_limites_m} m`
            : null,
        )}
      </div>

      {parcelAlternatives.length > 0 &&
        (hideInlinePicker ? (
          <div className="mt-5 pt-4 border-t border-ink-800/15 flex items-center justify-between gap-3">
            <span className="text-[12px] text-ink-600">
              Vous avez sélectionné cette parcelle parmi {allCandidates.length}{" "}
              candidates au début de l'analyse.
            </span>
            <button
              type="button"
              onClick={() => setMapOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-ink-900 px-3 py-1.5 text-[11.5px] font-semibold text-lime-300 hover:bg-ink-800 transition shrink-0"
            >
              <Ic.Pin size={11} />
              Modifier la parcelle
            </button>
          </div>
        ) : (
          <ParcelPicker
            candidates={allCandidates}
            selectedIds={selectedIdSet}
            onPick={onPickParcel}
            warn={isLarge}
            onOpenMap={() => setMapOpen(true)}
          />
        ))}
      {mapOpen && (
        <ParcelPickerMap
          candidates={allCandidates}
          initialSelectedIds={selectedIdSet}
          onCancel={() => setMapOpen(false)}
          onConfirm={(ids) => {
            setMapOpen(false);
            if (ids.length === 0) return;
            const picked = ids
              .map((id) => allCandidates.find((p) => p.id === id))
              .filter((p): p is CadastreParcel => !!p);
            if (picked.length === 0) return;
            // Single-pick → just hand the parcel to the existing onPick.
            // Multi-pick → merge into a composite "MULTI:" parcel so every
            // downstream consumer (fiche persistence, BD TOPO, Hero) keeps
            // the same CadastreParcel shape.
            onPickParcel(picked.length === 1 ? picked[0] : mergeParcels(picked));
          }}
        />
      )}
    </Card>
  );
};

// Inline picker shown when the cadastre buffer returned multiple candidates.
// Géocoding by postal address can snap to an adjacent parcel (private road,
// shared driveway, large domain) — this lets the user switch in one click.
const ParcelPicker = ({
  candidates,
  selectedIds,
  onPick,
  warn,
  onOpenMap,
}: {
  candidates: CadastreParcel[];
  /** Set of cadastre IDs the user currently has selected. Singleton for a
   *  regular pick, multiple for a MULTI composite coming out of the map
   *  picker — each component parcel in the list gets highlighted. */
  selectedIds: Set<string>;
  onPick: (p: CadastreParcel) => void;
  warn: boolean;
  onOpenMap: () => void;
}) => {
  return (
    <div className="mt-5 pt-4 border-t border-ink-800/15">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Ic.Bolt size={11} />
          <span className="text-[11px] font-mono uppercase tracking-widest text-ink-800">
            {candidates.length} parcelles proches — choisissez une ou
            plusieurs
          </span>
        </div>
        <button
          type="button"
          onClick={onOpenMap}
          className="inline-flex items-center gap-1.5 rounded-full bg-ink-900 px-3 py-1.5 text-[11.5px] font-semibold text-lime-300 hover:bg-ink-800 transition"
        >
          <Ic.Pin size={11} />
          Afficher sur la carte
        </button>
      </div>
      {warn && (
        <div className="mb-2 text-[11.5px] text-ink-900 bg-amber-100 border border-amber-300 rounded-md px-2.5 py-1.5 leading-snug">
          La parcelle sélectionnée fait plus de 1,5 ha. Vérifiez qu'il s'agit
          bien de la parcelle du bien (et non d'une voie privée ou d'un
          domaine voisin).
        </div>
      )}
      <ul className="space-y-1">
        {candidates.map((p) => {
          const isSel = selectedIds.has(p.id);
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onPick(p)}
                className={`w-full flex items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left transition ${
                  isSel
                    ? "bg-ink-900 text-white"
                    : "bg-white/70 hover:bg-white text-ink-900"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`w-3.5 h-3.5 rounded-full grid place-items-center ${
                      isSel ? "bg-lime-300" : "border border-ink-400"
                    }`}
                  >
                    {isSel && (
                      <span className="w-1.5 h-1.5 rounded-full bg-ink-900" />
                    )}
                  </span>
                  <span className="font-mono text-[12px]">
                    {(p.prefixe && p.prefixe !== "000" ? p.prefixe : "") +
                      p.section +
                      "-" +
                      p.numero}
                  </span>
                </span>
                <span className="text-[12px] tabular font-medium">
                  {p.surface_m2.toLocaleString("fr-FR")} m²
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
